import type { DirectorNotePlacement } from '@/types/domain'

const DIRECTOR_NOTE_PLACEMENTS = new Set<DirectorNotePlacement>(['append', 'prepend'])

interface MessageWithTime {
  id?: number | string
  t?: string
  created_at?: string
  updated_at?: string
  is_user_author?: boolean
  content?: string
  director_note?: DirectorNoteDraft | null
}

export interface DirectorNoteDraft {
  content: string
  placement: DirectorNotePlacement
  visibility_count: number | null
}

// Callers supply the full timeline in its authoritative order.
export function normalizeVisibilityCount(value: unknown): number | null {
  if (value === '' || value === undefined || value === null) {
    return null
  }

  const parsed = Number(value)
  if (!Number.isFinite(parsed) || parsed < 1) {
    return null
  }

  return Math.floor(parsed)
}

export function normalizeDirectorNote(raw: unknown): DirectorNoteDraft | null {
  if (!raw || typeof raw !== 'object') return null
  const r = raw as Record<string, unknown>

  const placement: DirectorNotePlacement = DIRECTOR_NOTE_PLACEMENTS.has(
    r.placement as DirectorNotePlacement
  )
    ? (r.placement as DirectorNotePlacement)
    : 'append'

  return {
    content: (r.content as { toString?: () => string } | null | undefined)?.toString?.() ?? '',
    placement,
    visibility_count: normalizeVisibilityCount(r.visibility_count),
  }
}

export function countLaterUserMessages(
  messages: MessageWithTime[] = [],
  sourceMessageId: number | string
): number {
  const sorted = messages
  const sourceIndex = sorted.findIndex((message) => String(message?.id) === String(sourceMessageId))

  if (sourceIndex === -1) return 0

  return sorted.slice(sourceIndex + 1).filter((message) => message?.is_user_author).length
}

export function isDirectorNoteActive({
  message,
  messages,
}: {
  message: MessageWithTime
  messages: MessageWithTime[]
}): boolean {
  const note = normalizeDirectorNote(message?.director_note)
  if (!note || !note.content.trim()) return false
  if (note.visibility_count == null) return true

  return countLaterUserMessages(messages, message?.id ?? '') < note.visibility_count
}

export function getDirectorNoteStatus({
  message,
  messages,
}: {
  message: MessageWithTime
  messages: MessageWithTime[]
}): 'none' | 'active' | 'expired' {
  const note = normalizeDirectorNote(message?.director_note)
  if (!note || !note.content.trim()) return 'none'
  return isDirectorNoteActive({ message, messages }) ? 'active' : 'expired'
}

function serializeTaggedSection(tag: string, value: string): string {
  return `<${tag}>\n${value}\n</${tag}>`
}

export function buildMessageContentWithDirectorNote({
  message,
  messages,
  contentOverride,
}: {
  message: MessageWithTime
  messages: MessageWithTime[]
  contentOverride?: string
}): string {
  const baseContent = contentOverride ?? message?.content ?? ''
  const note = normalizeDirectorNote(message?.director_note)

  if (!note || !note.content.trim()) {
    return baseContent
  }

  if (!isDirectorNoteActive({ message, messages })) {
    return baseContent
  }

  const taggedMessage = serializeTaggedSection('user_message', baseContent)
  const taggedNote = serializeTaggedSection('director_note', note.content.trim())

  if (note.placement === 'prepend') {
    return `${taggedNote}\n\n${taggedMessage}`
  }

  return `${taggedMessage}\n\n${taggedNote}`
}

export function createBlankDirectorNoteDraft(): DirectorNoteDraft {
  return {
    content: '',
    placement: 'append',
    visibility_count: 1,
  }
}

export function normalizeDirectorNoteDraft(raw: unknown): DirectorNoteDraft {
  const base = createBlankDirectorNoteDraft()
  if (!raw || typeof raw !== 'object') return base
  const r = raw as Record<string, unknown>

  return {
    content: (r.content as { toString?: () => string } | null | undefined)?.toString?.() ?? '',
    placement: DIRECTOR_NOTE_PLACEMENTS.has(r.placement as DirectorNotePlacement)
      ? (r.placement as DirectorNotePlacement)
      : base.placement,
    visibility_count: normalizeVisibilityCount(r.visibility_count) ?? base.visibility_count,
  }
}
